// Pure helpers for the Listings-redesign fields of My Business (DR-81…DR-86, FR-B30):
// the amenity picker, the social links, the "What's nearby" rows (DB-80 amendment),
// the category-specific `type_meta` (DB-103, listing-meta.yaml) and the "Details on
// file" line. No React, no I/O — unit-tested in listing-extras-model.test.ts.
import type { MessageKey } from '@bookingdei/i18n/message-key';
import type { Business, Category, NearbyPlace, TypeMeta } from '../../mock/seeds/schema-types';

type Lang = 'en' | 'bn';

// ---- amenities (DB-98) ------------------------------------------------------------------
export type AmenityOption = NonNullable<Category['amenities']>[number];

export function amenityLabel(a: Pick<AmenityOption, 'label_en' | 'label_bn'>, lang: Lang): string {
  return lang === 'bn' ? a.label_bn || a.label_en : a.label_en || a.label_bn;
}

/** The listing's keys that the vocabulary knows, in vocabulary order (unknown legacy strings are dropped). */
export function knownAmenities(
  keys: readonly string[] | undefined,
  vocabulary: readonly AmenityOption[],
): string[] {
  const set = new Set((keys ?? []).map((k) => k.trim().toLowerCase()));
  return vocabulary.filter((a) => set.has(a.key)).map((a) => a.key);
}

export function toggleAmenity(
  keys: readonly string[],
  key: string,
  vocabulary: readonly AmenityOption[],
): string[] {
  const next = new Set(keys);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return vocabulary.filter((a) => next.has(a.key)).map((a) => a.key);
}

export function sameStringList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

// ---- social links -----------------------------------------------------------------------
export const SOCIAL_NETWORKS = ['website', 'facebook', 'whatsapp', 'instagram', 'youtube'] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];
export type SocialDraft = Record<SocialNetwork, string>;

export function socialDraft(b: Pick<Business, 'social_links'>): SocialDraft {
  const links = b.social_links ?? {};
  return Object.fromEntries(SOCIAL_NETWORKS.map((n) => [n, links[n] ?? ''])) as SocialDraft;
}

export function sameSocial(a: SocialDraft, b: SocialDraft): boolean {
  return SOCIAL_NETWORKS.every((n) => a[n].trim() === b[n].trim());
}

/** The `social_links` body (every non-empty network), or `'bad'` naming no network when a value is not an http(s) URL. */
export function socialBody(draft: SocialDraft): Record<string, string> | { bad: SocialNetwork } {
  const out: Record<string, string> = {};
  for (const n of SOCIAL_NETWORKS) {
    const v = draft[n].trim();
    if (!v) continue;
    if (!/^https?:\/\/\S+$/i.test(v)) return { bad: n };
    out[n] = v;
  }
  return out;
}

// ---- what's nearby (DB-80, DB-104) ------------------------------------------------------
export const MAX_NEARBY = 6;
export const NEARBY_KINDS = [
  'transit',
  'market',
  'health',
  'landmark',
  'mosque',
  'attraction',
  'shopping',
  'beach',
  'park',
  'food',
  'other',
] as const;
export type NearbyKind = (typeof NEARBY_KINDS)[number];
export const NEARBY_KIND_KEY: Record<NearbyKind, MessageKey> = {
  transit: 'lr.merchant.nearby.kind.transit',
  market: 'lr.merchant.nearby.kind.market',
  health: 'lr.merchant.nearby.kind.health',
  landmark: 'lr.merchant.nearby.kind.landmark',
  mosque: 'lr.merchant.nearby.kind.mosque',
  attraction: 'lr.merchant.nearby.kind.attraction',
  shopping: 'lr.merchant.nearby.kind.shopping',
  beach: 'lr.merchant.nearby.kind.beach',
  park: 'lr.merchant.nearby.kind.park',
  food: 'lr.merchant.nearby.kind.food',
  other: 'lr.merchant.nearby.kind.other',
};

export interface NearbyRow {
  name: string;
  kind: NearbyKind;
  /** Metres, as typed (digits only). */
  distance: string;
}

export function nearbyRows(places: readonly NearbyPlace[] | undefined): NearbyRow[] {
  return (places ?? []).slice(0, MAX_NEARBY).map((p) => ({
    name: p.name,
    kind: (NEARBY_KINDS as readonly string[]).includes(p.category ?? '')
      ? (p.category as NearbyKind)
      : 'other',
    distance: String(p.distance_m),
  }));
}

export function newNearbyRow(): NearbyRow {
  return { name: '', kind: 'other', distance: '' };
}

/**
 * Rows → the PATCH `nearby` list: named rows with a distance, nearest first, at most six.
 * Blank rows are skipped; a named row without a valid distance (or vice versa) is the
 * first error, returned by index.
 */
export function nearbyBody(
  rows: readonly NearbyRow[],
): { places: NearbyPlace[] } | { badRow: number } {
  const places: NearbyPlace[] = [];
  for (const [i, r] of rows.entries()) {
    const name = r.name.trim();
    const dist = r.distance.trim();
    if (!name && !dist) continue;
    if (!name || !/^\d{1,6}$/.test(dist)) return { badRow: i };
    places.push({ name: name.slice(0, 120), category: r.kind, distance_m: Number(dist) });
  }
  places.sort((a, b) => a.distance_m - b.distance_m);
  return { places: places.slice(0, MAX_NEARBY) };
}

export function sameNearby(a: readonly NearbyRow[], b: readonly NearbyRow[]): boolean {
  const key = (rows: readonly NearbyRow[]) =>
    JSON.stringify(rows.map((r) => [r.name.trim(), r.kind, r.distance.trim()]));
  return key(a) === key(b);
}

/** DB-104: the score the server derives — shown read-only so the merchant sees what a row buys. */
export function walkScoreOf(places: readonly NearbyPlace[]): 0 | 1 | 2 {
  let score: 0 | 1 | 2 = 0;
  for (const p of places) {
    if (p.category !== 'transit' && p.category !== 'market') continue;
    if (p.distance_m <= 500) return 2;
    if (p.distance_m <= 1000) score = 1;
  }
  return score;
}

export const WALK_KEY: Record<0 | 1 | 2, MessageKey> = {
  0: 'lr.merchant.nearby.walk.0',
  1: 'lr.merchant.nearby.walk.1',
  2: 'lr.merchant.nearby.walk.2',
};

// ---- type_meta (DB-103) ----------------------------------------------------------------
/** The categories that carry a `type_meta` shape (listing-meta.yaml). */
export const TYPE_META_CATEGORIES = ['hotel', 'restaurant', 'turf'] as const;
export type TypeMetaCategory = (typeof TYPE_META_CATEGORIES)[number];

export function typeMetaCategory(categoryId: string): TypeMetaCategory | null {
  return (TYPE_META_CATEGORIES as readonly string[]).includes(categoryId)
    ? (categoryId as TypeMetaCategory)
    : null;
}

export const TURF_FACT_KEYS = [
  'format',
  'pitch_size',
  'surface',
  'setting',
  'lights',
  'power',
  'open',
] as const;
export const TURF_WHO_KEYS = ['general', 'women', 'family', 'kids'] as const;
export const TURF_PACKAGE_KEYS = [
  'tournament',
  'corporate',
  'monthly',
  'play_eat',
  'other',
] as const;
export const TURF_GO_KEYS = ['address', 'landmark', 'transit', 'rickshaw', 'parking'] as const;
export const TURF_PAY_KEYS = ['advance', 'confirmation', 'cancel', 'rain', 'other'] as const;
export const MAX_META_ROWS = 12;

export type TypeMetaDraft = Record<string, unknown>;

export function typeMetaDraft(b: Pick<Business, 'type_meta'>): TypeMetaDraft {
  const meta = b.type_meta;
  return meta && typeof meta === 'object'
    ? (JSON.parse(JSON.stringify(meta)) as TypeMetaDraft)
    : {};
}

export function sameTypeMeta(a: TypeMetaDraft, b: TypeMetaDraft): boolean {
  return JSON.stringify(cleanTypeMeta(a)) === JSON.stringify(cleanTypeMeta(b));
}

const trimText = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

/**
 * The draft → the wire object: blank strings and empty lists dropped, numbers kept when
 * finite, rows without their required English text removed. Deterministic key order so
 * a comparison is stable.
 */
export function cleanTypeMeta(draft: TypeMetaDraft): TypeMeta {
  const out: Record<string, unknown> = {};
  const text = (k: string) => {
    const v = trimText(draft[k]);
    if (v !== undefined) out[k] = v;
  };
  const num = (k: string) => {
    const v = draft[k];
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
    if (Number.isFinite(n) && n > 0) out[k] = Math.round(n);
  };
  const list = (
    k: string,
    required: readonly string[],
    optional: readonly string[] = [],
    fixed: Record<string, (v: unknown) => unknown> = {},
  ) => {
    const rows = Array.isArray(draft[k]) ? (draft[k] as unknown[]) : [];
    const kept: Record<string, unknown>[] = [];
    for (const raw of rows) {
      if (!raw || typeof raw !== 'object') continue;
      const r = raw as Record<string, unknown>;
      const row: Record<string, unknown> = {};
      let ok = true;
      for (const [f, fn] of Object.entries(fixed)) row[f] = fn(r[f]);
      for (const f of required) {
        const v = trimText(r[f]);
        if (v === undefined) {
          ok = false;
          break;
        }
        row[f] = v;
      }
      if (!ok) continue;
      for (const f of optional) {
        const v = trimText(r[f]);
        if (v !== undefined) row[f] = v;
      }
      kept.push(row);
      if (kept.length >= MAX_META_ROWS) break;
    }
    if (kept.length) out[k] = kept;
  };
  // Hotel.
  for (const k of ['check_in', 'check_out']) text(k);
  for (const k of ['floors', 'rooms_total', 'seating']) num(k);
  // Restaurant.
  if (Array.isArray(draft.cuisine)) {
    const cuisine = (draft.cuisine as unknown[])
      .map(trimText)
      .filter((v): v is string => !!v)
      .slice(0, MAX_META_ROWS);
    if (cuisine.length) out.cuisine = cuisine;
  }
  text('reservation_note');
  // Turf.
  list('facts', ['value_en'], ['value_bn'], {
    key: (v) => ((TURF_FACT_KEYS as readonly string[]).includes(String(v)) ? v : 'format'),
  });
  for (const k of ['ramadan_en', 'ramadan_bn', 'bonus_en', 'bonus_bn']) text(k);
  list('who', ['title_en', 'detail_en'], ['title_bn', 'detail_bn'], {
    key: (v) => ((TURF_WHO_KEYS as readonly string[]).includes(String(v)) ? v : 'general'),
  });
  list('ground_rules', ['text_en'], ['text_bn'], { allowed: (v) => v === true });
  list(
    'packages',
    ['title_en', 'detail_en', 'price_label_en'],
    ['title_bn', 'detail_bn', 'price_label_bn'],
    { key: (v) => ((TURF_PACKAGE_KEYS as readonly string[]).includes(String(v)) ? v : 'other') },
  );
  list('getting_there', ['text_en'], ['text_bn'], {
    key: (v) => ((TURF_GO_KEYS as readonly string[]).includes(String(v)) ? v : 'landmark'),
  });
  list('faq', ['q_en', 'a_en'], ['q_bn', 'a_bn']);
  list('pay', ['title_en', 'text_en'], ['title_bn', 'text_bn'], {
    key: (v) => ((TURF_PAY_KEYS as readonly string[]).includes(String(v)) ? v : 'other'),
  });
  if (Array.isArray(draft.not_available)) {
    const na = (draft.not_available as unknown[])
      .map(trimText)
      .filter((v): v is string => !!v)
      .slice(0, MAX_META_ROWS);
    if (na.length) out.not_available = na;
  }
  return out as TypeMeta;
}

/** The keys of `cleanTypeMeta` a category's shape accepts — the draft keeps only those when the category changes. */
export const TYPE_META_KEYS: Record<TypeMetaCategory, readonly string[]> = {
  hotel: ['check_in', 'check_out', 'floors', 'rooms_total'],
  restaurant: ['cuisine', 'seating', 'reservation_note'],
  turf: [
    'facts',
    'ramadan_en',
    'ramadan_bn',
    'bonus_en',
    'bonus_bn',
    'who',
    'ground_rules',
    'packages',
    'getting_there',
    'faq',
    'pay',
    'not_available',
  ],
};

export function typeMetaFor(categoryId: string, draft: TypeMetaDraft): TypeMeta {
  const cat = typeMetaCategory(categoryId);
  const clean = cleanTypeMeta(draft) as Record<string, unknown>;
  if (!cat) return {};
  return Object.fromEntries(
    Object.entries(clean).filter(([k]) => TYPE_META_KEYS[cat].includes(k)),
  ) as TypeMeta;
}

// ---- details on file (DR-87) ------------------------------------------------------------
export const MISSING_KEY: Record<'photos' | 'about' | 'amenities' | 'price' | 'hours', MessageKey> =
  {
    photos: 'lr.merchant.details.missing.photos',
    about: 'lr.merchant.details.missing.about',
    amenities: 'lr.merchant.details.missing.amenities',
    price: 'lr.merchant.details.missing.price',
    hours: 'lr.merchant.details.missing.hours',
  };

/** The listing with the lowest "Details on file" score that is not complete, for the Home checklist line. */
export function leastComplete(businesses: readonly Business[]): Business | null {
  let pick: Business | null = null;
  for (const b of businesses) {
    const score = b.details?.score;
    if (typeof score !== 'number' || score >= 5) continue;
    if (!pick || score < (pick.details?.score ?? 5)) pick = b;
  }
  return pick;
}
